import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import ContentBox from '../components/ContentBox';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const API_URL = process.env.REACT_APP_API_URL;

function WatchModal({ isOpen, onClose, isLoggedIn, furnitureId, furnitureName }) {
    const [subscribed, setSubscribed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);

    useEffect(() => {
        if (!isOpen || !isLoggedIn || !furnitureId) return;

        const checkStatus = async () => {
            setCheckingStatus(true);
            try {
                const response = await fetch(`${API_URL}subscriptions/status/${furnitureId}`, {
                    credentials: 'include',
                });
                const data = await response.json();
                if (data.success) setSubscribed(data.subscribed);
            } catch {
                // Silently fail — user can still try subscribing
            } finally {
                setCheckingStatus(false);
            }
        };

        checkStatus();
    }, [isOpen, isLoggedIn, furnitureId]);

    const handleSubscribe = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}subscriptions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ furniId: furnitureId }),
            });
            const data = await response.json();

            if (data.success) {
                setSubscribed(true);
                toast.success(`You're now watching ${furnitureName}!`);
            } else if (response.status === 409) {
                setSubscribed(true);
                toast.info('You are already subscribed to this item.');
            } else if (data.notVerified) {
                toast.error('You need to verify with OriginsBot in the FranklyOrigins Discord first!', {
                    autoClose: 6000,
                });
            } else {
                toast.error(data.message || 'Failed to subscribe');
            }
        } catch {
            toast.error('Could not connect to server');
        } finally {
            setLoading(false);
        }
    };

    const handleUnsubscribe = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}subscriptions/${furnitureId}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            const data = await response.json();

            if (data.success) {
                setSubscribed(false);
                toast.success('Unsubscribed successfully.');
            } else {
                toast.error(data.message || 'Failed to unsubscribe');
            }
        } catch {
            toast.error('Could not connect to server');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={onClose}
            ></div>

            <div
                className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-md w-full mx-4 z-[101]"
                style={{
                    boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.5)'
                }}
            >
                <div className="flex items-center gap-2 mb-4">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-maroon"
                    >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    <h2 className="text-maroon uppercase font-['Jersey_10'] text-[17px] tracking-[1px]">
                        Watch This Furni
                    </h2>
                </div>

                <hr className="border-gold mb-4" />

                {!isLoggedIn ? (
                    <>
                        <div className="mb-6">
                            <p className="text-gray-800 font-['Jersey_10'] text-[17px]">
                                You can watch this furni. This means that whenever the value changes, we will notify you based on your preference.
                            </p>
                        </div>

                        <div className="flex justify-end">
                            <button
                                onClick={onClose}
                                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-colors"
                                style={{
                                    boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </>
                ) : checkingStatus ? (
                    <p className="text-gray-600 font-['Jersey_10'] text-[15px] mb-4">Checking subscription status...</p>
                ) : subscribed ? (
                    <>
                        <div className="mb-6 space-y-3">
                            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-300 rounded">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600 flex-shrink-0">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <polyline points="22 4 12 14.01 9 11.01" />
                                </svg>
                                <p className="text-green-800 font-['Jersey_10'] text-[15px]">
                                    You're watching <strong>{furnitureName}</strong>. You'll be pinged on Discord when the value changes.
                                </p>
                            </div>
                            <p className="text-gray-600 text-sm">
                                You can cancel this subscription anytime in your user settings.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleUnsubscribe}
                                disabled={loading}
                                className="flex-1 bg-red-600 hover:opacity-90 disabled:opacity-50 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-opacity"
                                style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                            >
                                {loading ? 'Please wait...' : 'Unsubscribe'}
                            </button>
                            <button
                                onClick={onClose}
                                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-colors"
                                style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                            >
                                Close
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="mb-6 space-y-3">
                            <p className="text-gray-800 font-['Jersey_10'] text-[17px]">
                                Watch <strong>{furnitureName}</strong> and get pinged in the FranklyOrigins Discord whenever the value changes.
                            </p>
                            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded">
                                <p className="text-indigo-800 text-sm font-semibold mb-1">Discord Notification</p>
                                <p className="text-indigo-700 text-xs">
                                    You must be a member of the FranklyOrigins Discord server to receive pings. If you're not in the server yet, join us first!
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={handleSubscribe}
                                disabled={loading}
                                className="w-full bg-indigo-600 hover:opacity-90 disabled:opacity-50 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-opacity flex items-center justify-center gap-2"
                                style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                            >
                                {loading ? 'Subscribing...' : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 127.14 96.36" fill="white">
                                            <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
                                        </svg>
                                        Watch via Discord
                                    </>
                                )}
                            </button>
                            <button
                                onClick={onClose}
                                className="w-full bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-colors"
                                style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                            >
                                Cancel
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function FurnitureDetail({ userData }) {
    const { slug } = useParams();
    const [furniture, setFurniture] = useState(null);
    const [valueHistory, setValueHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState('30d');
    const [watchModalOpen, setWatchModalOpen] = useState(false);

    const isLoggedIn = !!userData;

    useEffect(() => {
        loadFurnitureDetail();
    }, [slug]);

    const loadFurnitureDetail = async () => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}furniture/${slug}`);
            const data = await response.json();

            if (data.success) {
                setFurniture(data.furniture);
                setValueHistory(data.valueHistory);
            }
        } catch (error) {
            console.error('Error loading furniture detail:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatChartDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short'
        });
    };

    const fillDataGaps = (history, days) => {
        if (history.length === 0) return [];

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);

        const firstValueDate = new Date(history[0].timestamp);
        const actualStartDate = firstValueDate > startDate ? firstValueDate : startDate;

        const filledData = [];
        let currentValueIndex = 0;

        for (let d = new Date(actualStartDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            const currentDate = new Date(d);

            while (
                currentValueIndex < history.length - 1 &&
                new Date(history[currentValueIndex + 1].timestamp) <= currentDate
                ) {
                currentValueIndex++;
            }

            if (currentValueIndex < history.length && new Date(history[currentValueIndex].timestamp) <= currentDate) {
                filledData.push({
                    date: currentDate.toISOString().split('T')[0],
                    min: history[currentValueIndex].min,
                    max: history[currentValueIndex].max,
                    average: history[currentValueIndex].average
                });
            }
        }

        return filledData;
    };

    const getChartData = () => {
        const daysMap = {
            '7d': 7,
            '30d': 30,
            '1y': 365
        };

        const days = daysMap[timeRange];
        const filledData = fillDataGaps(valueHistory, days);

        if (filledData.length === 0) {
            return {
                labels: [],
                datasets: []
            };
        }

        return {
            labels: filledData.map(d => formatChartDate(d.date)),
            datasets: [
                {
                    label: 'Min Value',
                    data: filledData.map(d => d.min),
                    borderColor: 'rgb(136, 8, 8)',
                    backgroundColor: 'rgba(136, 8, 8, 0.1)',
                    tension: 0.1,
                    fill: false
                },
                {
                    label: 'Average Value',
                    data: filledData.map(d => d.average),
                    borderColor: 'rgb(234, 179, 8)',
                    backgroundColor: 'rgba(234, 179, 8, 0.1)',
                    tension: 0.1,
                    fill: false,
                    borderWidth: 2
                },
                {
                    label: 'Max Value',
                    data: filledData.map(d => d.max),
                    borderColor: 'rgb(34, 197, 94)',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    tension: 0.1,
                    fill: false
                }
            ]
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    font: {
                        family: "'Jersey 10', sans-serif",
                        size: 12
                    }
                }
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function(context) {
                        return context.dataset.label + ': ' + context.parsed.y + ' Habloons';
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function(value) {
                        return value + ' H';
                    }
                }
            },
            x: {
                ticks: {
                    maxRotation: 45,
                    minRotation: 45
                }
            }
        }
    };

    const calculateAnalytics = () => {
        if (valueHistory.length === 0) return null;

        const prices = valueHistory.map(v => v.average);
        const firstPrice = prices[0];
        const lastPrice = prices[prices.length - 1];
        const change = lastPrice - firstPrice;
        const changePercent = ((change / firstPrice) * 100).toFixed(1);

        const highestAvg = Math.max(...prices);
        const lowestAvg = Math.min(...prices);
        const volatility = highestAvg - lowestAvg;

        const trend = lastPrice > firstPrice ? 'Increasing' :
            lastPrice < firstPrice ? 'Decreasing' : 'Stable';

        return {
            change,
            changePercent,
            highestAvg,
            lowestAvg,
            volatility,
            trend
        };
    };

    const analytics = furniture && valueHistory.length > 0 ? calculateAnalytics() : null;

    if (loading) {
        return (
            <div className="w-full min-h-screen p-4 lg:p-6">
                <ContentBox theme="default" tint="lightskyblue" header="Loading...">
                    <p className="text-white">Loading furniture details...</p>
                </ContentBox>
            </div>
        );
    }

    if (!furniture) {
        return (
            <div className="w-full min-h-screen p-4 lg:p-6">
                <ContentBox theme="default" tint="red" header="Error">
                    <p className="text-white">Furniture not found</p>
                    <Link to="/values" className="text-blue-300 underline mt-4 inline-block">
                        ← Back to Values
                    </Link>
                </ContentBox>
            </div>
        );
    }

    return (
        <div className="w-full min-h-screen p-4 lg:p-6">
            <WatchModal
                isOpen={watchModalOpen}
                onClose={() => setWatchModalOpen(false)}
                isLoggedIn={isLoggedIn}
                furnitureId={furniture.id}
                furnitureName={furniture.name}
            />

            <div className="max-w-7xl mx-auto space-y-6">
                {/* Back Button & Watch Button */}
                <div className=" bg-gray-300 p-2 rounded flex gap-2 items-center">
                    <Link
                        to="/values"
                        className="h-9 px-3 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-all font-['Jersey_10'] uppercase text-sm tracking-wide flex items-center"
                        style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                    >
                        ← Back to Values
                    </Link>

                    <div className="flex flex-row ml-auto">
                        <button
                            onClick={() => setWatchModalOpen(true)}
                            className="h-9 px-3 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-all flex items-center gap-2 font-['Jersey_10'] uppercase text-sm tracking-wide"
                            style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                            title="Watch Furni"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                            </svg>

                            <span>Watch Furni</span>
                        </button>
                    </div>
                </div>

                {/* Main Info Section */}
                <div className="flex flex-col lg:flex-row gap-6 lg:items-stretch">
                    {/* Image & Basic Info */}
                    <div className="lg:w-4/12 flex min-h-[350px] lg:min-h-0">
                        <div className="flex-1 [&>*]:h-full">
                            <ContentBox theme="default" tint="gold" header={furniture.name}>
                                <div className="flex flex-col items-center">
                                    {/* Image in gray container */}
                                    <div className="mb-4 bg-gray-200/50 p-4 rounded w-full flex items-center justify-center">
                                        {furniture.largeImg ? (
                                            <img
                                                src={furniture.largeImg}
                                                alt={furniture.name}
                                                className="max-w-full"
                                            />
                                        ) : (
                                            <span className="text-gray-500">No image available</span>
                                        )}
                                    </div>

                                    <div className="w-full space-y-1">
                                        <div className="bg-gray-200/50 p-2 rounded">
                                            <span className="text-xs text-gray-600">Category:</span>
                                            <span className="ml-2 font-bold text-gray-900">{furniture.categoryName}</span>
                                        </div>

                                        {furniture.releasePrice && (
                                            <div className="bg-gray-200/50 p-2 rounded">
                                                <span className="text-xs text-gray-600">Release Price:</span>
                                                <span className="ml-2 font-bold text-gray-900">{furniture.releasePrice} Credits</span>
                                            </div>
                                        )}

                                        <div className="bg-gray-200/50 p-2 rounded">
                                            <span className="text-xs text-gray-600">Released:</span>
                                            <span className="ml-2 font-bold text-gray-900">{formatDate(furniture.releaseDate)}</span>
                                        </div>

                                        <div className="bg-gray-200/50 p-2 rounded">
                                            <span className="text-xs text-gray-600">Status:</span>
                                            <span className="ml-2 font-bold text-gray-900">
                                {furniture.removeDate ? `Removed ${formatDate(furniture.removeDate)}` : 'Available'}
                            </span>
                                        </div>
                                    </div>

                                    {furniture.description && (
                                        <div className="w-full mt-4 p-3 bg-gray-200/50 rounded">
                                            <p className="text-sm text-gray-700 italic">{furniture.description}</p>
                                        </div>
                                    )}

                                    {furniture.info && (
                                        <div className="w-full mt-2 p-3 bg-gray-200/50 rounded">
                                            <p className="text-xs text-gray-600 whitespace-pre-wrap">{furniture.info}</p>
                                        </div>
                                    )}
                                </div>
                            </ContentBox>
                        </div>
                    </div>

                    {/* Current Values */}
                    <div className="lg:w-8/12 flex flex-col gap-y-2 self-stretch">
                        <ContentBox theme="default" tint="lightskyblue" header="Current Value">
                            {!furniture.hasValues ? (
                                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                                    <p className="font-bold">{furniture.name} isn't valued yet!</p>
                                    <p className="text-sm mt-2">
                                        We don't have any trading data for this item yet, or it has just been released.
                                        Don't worry, we'll have values soon!
                                    </p>
                                </div>
                            ) : (
                                <div className="flex justify-center">
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-5xl w-full">
                                        <div className="bg-gray-300/40 p-4 rounded text-center">
                                            <div className="text-xs text-gray-600 mb-1">Min</div>
                                            <div className="text-2xl font-bold text-gray-900">{furniture.currentMin}</div>
                                            <div className="text-xs text-gray-500">Habloons</div>
                                        </div>

                                        <div className="bg-gray-300/40 p-4 rounded text-center">
                                            <div className="text-xs text-gray-600 mb-1">Average</div>
                                            <div className="text-2xl font-bold text-gray-900">{furniture.currentAvg}</div>
                                            <div className="text-xs text-gray-500">Habloons</div>
                                        </div>

                                        <div className="bg-gray-300/40 p-4 rounded text-center">
                                            <div className="text-xs text-gray-600 mb-1">Max</div>
                                            <div className="text-2xl font-bold text-gray-900">{furniture.currentMax}</div>
                                            <div className="text-xs text-gray-500">Habloons</div>
                                        </div>

                                        <div className="bg-gray-300/40 p-4 rounded text-center">
                                            <div className="text-xs text-gray-600 mb-1">AVG HC Value</div>
                                            <div className="text-2xl font-bold text-gray-900">{furniture.currentHCValue}</div>
                                            <div className="text-xs text-gray-500">HC</div>
                                        </div>

                                        <div className="bg-gray-300/40 p-4 rounded text-center">
                                            <div className="text-xs text-gray-600 mb-1">AVG Dino Value</div>
                                            <div className="text-2xl font-bold text-gray-900">{furniture.currentDinoValue}</div>
                                            <div className="text-xs text-gray-500">Dinos</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </ContentBox>

                        {/* Analytics */}
                        {analytics && (
                            <div className="flex-1 min-h-0 flex flex-col">
                                <ContentBox theme="default" tint="green" header="Market Analysis" className="flex-1 !mb-0 flex flex-col" contentClassName="flex-1 flex flex-col">
                                    <div className="bg-gray-300/40 p-6 rounded flex-1">
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
                                            <div>
                                                <div className="text-xs text-gray-600 mb-1">Price Change</div>
                                                <div className={`text-xl font-bold ${analytics.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {analytics.change >= 0 ? '+' : ''}{analytics.change}
                                                </div>
                                                <div className="text-xs text-gray-500">({analytics.changePercent}%)</div>
                                            </div>

                                            <div>
                                                <div className="text-xs text-gray-600 mb-1">Overall Trend</div>
                                                <div className={`text-xl font-bold ${
                                                    analytics.trend === 'Increasing' ? 'text-green-600' :
                                                        analytics.trend === 'Decreasing' ? 'text-red-600' : 'text-gray-600'
                                                }`}>
                                                    {analytics.trend}
                                                </div>
                                            </div>

                                            <div>
                                                <div className="text-xs text-gray-600 mb-1">Volatility</div>
                                                <div className="text-xl font-bold text-gray-900">{analytics.volatility}</div>
                                                <div className="text-xs text-gray-500">Habloons spread</div>
                                            </div>

                                            <div>
                                                <div className="text-xs text-gray-600 mb-1">All-Time High</div>
                                                <div className="text-xl font-bold text-gray-900">{analytics.highestAvg}</div>
                                                <div className="text-xs text-gray-500">Habloons</div>
                                            </div>

                                            <div>
                                                <div className="text-xs text-gray-600 mb-1">All-Time Low</div>
                                                <div className="text-xl font-bold text-gray-900">{analytics.lowestAvg}</div>
                                                <div className="text-xs text-gray-500">Habloons</div>
                                            </div>

                                            <div>
                                                <div className="text-xs text-gray-600 mb-1">Total Updates</div>
                                                <div className="text-xl font-bold text-gray-900">{valueHistory.length}</div>
                                                <div className="text-xs text-gray-500">Price changes</div>
                                            </div>
                                        </div>
                                    </div>
                                </ContentBox>
                            </div>
                        )}
                    </div>
                </div>

                {/* Price History Chart */}
                {furniture.hasValues && (
                    <ContentBox theme="default" tint="maroon" header="Price History">
                        <div className="mb-4 flex gap-2">
                            {['7d', '30d', '1y'].map((range) => (
                                <button
                                    key={range}
                                    onClick={() => setTimeRange(range)}
                                    className={`px-4 py-2 rounded transition-all font-['Jersey_10'] uppercase text-sm ${
                                        timeRange === range
                                            ? 'bg-maroon text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                    style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                                >
                                    {range === '7d' ? 'Last Week' : range === '30d' ? 'Last Month' : 'Last Year'}
                                </button>
                            ))}
                        </div>

                        <div className="bg-white p-4 rounded" style={{ height: '400px' }}>
                            <Line data={getChartData()} options={chartOptions} />
                        </div>

                        <div className="mt-4 text-xs text-gray-300">
                            * Chart shows filled data points. Days without updates display the previous known value.
                        </div>
                    </ContentBox>
                )}
            </div>
        </div>
    );
}

export default FurnitureDetail;